import React from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useProjectElementListStore } from '../../shared/store/projectElementStore';
import SelectBox from '../../shared/component/SelectBox';
import { getAlignment } from '../../shared/component/headless/textarea/TextAreaBody';
import { TextBoxData } from '../../shared/dto/EntityRepository';
import { DisplayAlignment, DisplaySize, SelectType, TextAlignment } from '../../shared/enum/EnumRepository';
import ConstrainedRichText from '../../shared/richText/ConstrainedRichText';
import { PEHeader, PEIndicator, SelectBoxContainer, SelectBoxWrapper } from './Work';
import { TEXTBOX_CONTENT_MAX_LENGTH } from '../../shared/constants/limits';

export interface TextBoxProps {
  peId: string;
  displayAlignment: DisplayAlignment;
  displaySize: DisplaySize;
  data: TextBoxData;
}

const TextBox: React.FC<TextBoxProps> = ({ peId, displayAlignment, displaySize, data }) => {
  const { isEditMode } = useEditMode();
  const handleChange = useProjectElementListStore((s) => s.updateTextBox);
  const updateDisplayAlignment = useProjectElementListStore((s) => s.updateDisplayAlignment);
  const handleDisplaySizeChange = useProjectElementListStore((s) => s.updateDisplaySize);
  const effectiveDisplaySize = displaySize || DisplaySize.BIG;
  const effectiveDisplayAlignment = displayAlignment || DisplayAlignment.CENTER;

  return (
    <TextBoxWrapper>
      {isEditMode &&
      <PEHeader>
        <SelectBoxContainer>
          <SelectBoxWrapper>
            <SelectBox
              value={effectiveDisplaySize}
              selectType={SelectType.WORK_SIZE}
              handleChange={(value) => handleDisplaySizeChange(peId, value)}
              direction={false} />
          </SelectBoxWrapper>
          <SelectBoxWrapper>
            <SelectBox
              value={effectiveDisplayAlignment}
              selectType={SelectType.DISPLAY_ALIGNMENT}
              handleChange={(value) => updateDisplayAlignment(peId, value)}
              direction={false} />
          </SelectBoxWrapper>
        </SelectBoxContainer>
        <PEIndicator peId={peId}>: TextBox</PEIndicator>
        </PEHeader>
      }
      <TextBoxContentWrapper>
        <TextBoxSpacer $flexGrow={getLeftFlexGrow(effectiveDisplayAlignment)} />
        <TextBoxColumn $displaySize={effectiveDisplaySize} $isEditMode={isEditMode}>
          <ConstrainedRichText
            value={data.content}
            onChange={(next) => handleChange(peId, { content: next })}
            StyledView={TextBoxContent}
            maxLength={TEXTBOX_CONTENT_MAX_LENGTH}
            maxRows={100}
          />
        </TextBoxColumn>
        <TextBoxSpacer $flexGrow={getRightFlexGrow(effectiveDisplayAlignment)} />
      </TextBoxContentWrapper>
    </TextBoxWrapper>
  );
}

export const TextBoxWrapper = styled.div`
  position: relative;
  width: 100%;
  min-height: 80px;
`;

const getTextColumnWidth = (displaySize: DisplaySize): string => {
  switch (displaySize) {
    case DisplaySize.WIDE:
      return 'calc(100% - 16vw)';
    case DisplaySize.BIG:
      return 'clamp(900px, 70vw, 1000px)';
    case DisplaySize.MEDIUM:
      return 'clamp(750px, 60vw, 800px)';
    case DisplaySize.REGULAR:
      return 'clamp(600px, 50vw, 650px)';
    case DisplaySize.SMALL:
      return 'clamp(400px, 40vw, 450px)';
    default:
      return 'calc(100% - 80vw)';
  }
};

const getLeftFlexGrow = (alignment: DisplayAlignment): string => {
  switch (alignment) {
    case DisplayAlignment.LEFT_FLUSH:
      return '0';
    case DisplayAlignment.LEFT_OFFSET:
      return '2';
    case DisplayAlignment.RIGHT_OFFSET:
      return '6';
    case DisplayAlignment.RIGHT_FLUSH:
      return '1';
    case DisplayAlignment.CENTER:
    default:
      return '1';
  }
};

const getRightFlexGrow = (alignment: DisplayAlignment): string => {
  switch (alignment) {
    case DisplayAlignment.LEFT_FLUSH:
      return '1';
    case DisplayAlignment.LEFT_OFFSET:
      return '6';
    case DisplayAlignment.RIGHT_OFFSET:
      return '2';
    case DisplayAlignment.RIGHT_FLUSH:
      return '0';
    case DisplayAlignment.CENTER:
    default:
      return '1';
  }
};

const TextBoxContentWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  --rich-text-size-s: ${({ theme }) => theme.fontSize.font_B04};
  --rich-text-size-l: ${({ theme }) => theme.fontSize.font_B02};
`;

const TextBoxSpacer = styled.div<{ $flexGrow: string }>`
  display: flex;
  flex: ${({ $flexGrow }) => `${$flexGrow} 1 0%`};
  min-width: 0;
  transition: all 0.5s ease;
`;

const TextBoxColumn = styled.div<{ $displaySize: DisplaySize; $isEditMode: boolean }>`
  flex-grow: 0;
  flex-shrink: 0;
  width: ${({ $displaySize }) => getTextColumnWidth($displaySize)};
  border-bottom: ${({ $isEditMode, theme }) => $isEditMode ? `1px solid ${theme.colors.color_Gray_05}` : 'none'};
  transition: width 0.6s ease;
`;

const TextBoxContent = styled.div<{ $alignment: TextAlignment }>`
  padding: 12px 0 4px 0;
  overflow-wrap: break-word;
  word-break: break-word;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  text-align: ${({ $alignment }) => getAlignment($alignment)};
  ${({ theme }) => theme.typography.Body_03_2};
  line-height: 1;
`;

export default TextBox;
